import React, { useEffect, useRef, useState } from 'react';
import { Wrapper, HeaderBar, Footer } from './Shared';
import { HOST_NAME, HOST_FIRST, HOST_PHOTO_URL, TYP_VIDEO_EMBED_URL, TYP_VIDEO_POSTER_URL, TYP_YOUTUBE_URL, WHATSAPP_URL } from './config';
import { track, firstName } from './pixel';
import { calendarSrc, loadGhlEmbedScript } from './calendar';
declare global {
  interface Window {
    YT?: any;
    onYouTubeIframeAPIReady?: () => void;
  }
}
function youtubeId(url: string) {
  if (!url) return '';
  const m = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/|live\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : /^[A-Za-z0-9_-]{11}$/.test(url) ? url : '';
}
function loadYouTubeApi(): Promise<any> {
  return new Promise(resolve => {
    if (window.YT && window.YT.Player) return resolve(window.YT);
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      prev && prev();
      resolve(window.YT);
    };
    if (!document.getElementById('yt-iframe-api')) {
      const s = document.createElement('script');
      s.id = 'yt-iframe-api';
      s.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(s);
    }
  });
}
function SpeakerIcon() {
  return <svg viewBox="0 0 124 124" className="w-12 h-12 sm:w-[83px] sm:h-[83px] mx-auto mb-1 sm:mb-2.5" data-id="element-894" data-mp="r5u42stgzrfg">
      <path d="M15.9429 51.4483V72.027C15.9429 74.8566 18.258 77.1717 21.0876 77.1717H36.5216L53.4477 94.0978C56.6889 97.3389 62.2451 95.0238 62.2451 90.445V32.9788C62.2451 28.4 56.6889 26.0849 53.4477 29.3261L36.5216 46.3036H21.0876C18.258 46.3036 15.9429 48.6187 15.9429 51.4483Z" fill="#fff" data-id="element-895" data-mp="m5o9p4vtfqto" />
      <path className="wave1" d="M77.8535 44.6574C75.6575 42.6526 72.5352 44.5033 72.5352 47.4769V48.7404C72.5352 49.7317 72.9181 50.6765 73.5206 51.4637C75.7313 54.3518 77.0176 57.8017 77.0176 61.5208C77.0176 65.2383 75.7325 68.6867 73.5237 71.5684C72.9195 72.3566 72.5352 73.303 72.5352 74.2961V75.9864C72.5352 78.9505 75.641 80.8028 77.8369 78.812C82.4786 74.604 85.3969 68.5285 85.3969 61.7378C85.3969 54.9552 82.4856 48.8862 77.8535 44.6574Z" fill="#fff" data-id="element-896" data-mp="8fcf8zbrdfzd" />
      <path className="wave2" d="M72.5352 22.8949V23.9239C72.5352 25.8789 73.8213 27.5766 75.622 28.2969C88.8953 33.5959 98.2586 46.612 98.2586 61.7374C98.2586 76.8628 88.8953 89.8789 75.622 95.1779C73.7699 95.8982 72.5352 97.5959 72.5352 99.5509V100.58C72.5352 103.821 75.7763 106.085 78.7602 104.953C96.2008 98.3162 108.548 81.493 108.548 61.7374C108.548 41.9818 96.2008 25.1586 78.7602 18.5219C75.7763 17.3387 72.5352 19.6538 72.5352 22.8949Z" fill="#fff" data-id="element-897" data-mp="1m0615teu2twj" />
    </svg>;
}
function UnmuteBox() {
  return <div className="absolute inset-0 flex items-center justify-center p-[6%] pointer-events-none" data-id="element-898" data-mp="184c6ofet472y">
      <div className="unmute-box w-[62%] min-w-[200px] aspect-[16/9] flex flex-col items-center justify-center rounded-2xl border-2 border-white bg-brand/80 px-3 text-center text-white" data-id="element-899" data-mp="1d3x5mptz8ffo">
        <SpeakerIcon data-id="element-900" data-mp="keztz5k9tgvw" />
        <h4 className="font-bold text-[13px] sm:text-[24px] md:text-[28px] leading-tight" data-id="element-901" data-mp="pfxcxkd3ueyg">Video Reproduciéndose<br data-id="element-902" data-mp="32q6v2pcqqrxf" />Haz Clic Para Activar el Sonido</h4>
      </div>
    </div>;
}
function YouTubeSlot({
  id
}: {
  id: string;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const [unmuted, setUnmuted] = useState(false);
  useEffect(() => {
    let cancelled = false;
    loadYouTubeApi().then(YT => {
      if (cancelled || !hostRef.current) return;
      playerRef.current = new YT.Player(hostRef.current, {
        videoId: id,
        width: '100%',
        height: '100%',
        host: 'https://www.youtube-nocookie.com',
        playerVars: {
          autoplay: 1,
          mute: 1,
          controls: 0,
          playsinline: 1,
          rel: 0,
          modestbranding: 1,
          iv_load_policy: 3,
          disablekb: 1,
          fs: 0
        },
        events: {
          onReady: (e: any) => {
            e.target.mute();
            e.target.playVideo();
          }
        }
      });
    });
    return () => {
      cancelled = true;
      try {
        playerRef.current?.destroy();
      } catch {}
    };
  }, [id]);
  const activate = () => {
    const p = playerRef.current;
    if (p && p.unMute) {
      p.unMute();
      p.setVolume(100);
      p.seekTo(0, true);
      p.playVideo();
    }
    setUnmuted(true);
  };
  return <div className="relative aspect-video rounded-2xl overflow-hidden bg-black" data-id="element-903" data-mp="182brbjm1ob">
      <div className="absolute inset-0 [&>iframe]:w-full [&>iframe]:h-full" data-id="element-904" data-mp="501dve79rilu"><div ref={hostRef} data-id="element-905" data-mp="3mdop4zjpvfny" /></div>
      {!unmuted && <div role="button" tabIndex={0} aria-label="Activar sonido" onClick={activate} onKeyDown={e => {
      if (e.key === 'Enter') activate();
    }} className="absolute inset-0 cursor-pointer" data-id="element-906" data-mp="3rdjdavc3xr5b">
          <div className="absolute inset-0 bg-black/20" data-id="element-907" data-mp="3icv1m7a6s2vl" />
          <UnmuteBox data-id="element-908" data-mp="3idmpevxjkwbb" />
        </div>}
    </div>;
}
function GenericSlot() {
  const [playing, setPlaying] = useState(false);
  // Mientras no haya video, se usa la foto de Chale como portada
  const poster = TYP_VIDEO_POSTER_URL || HOST_PHOTO_URL;
  const src = TYP_VIDEO_EMBED_URL ? TYP_VIDEO_EMBED_URL + (TYP_VIDEO_EMBED_URL.includes('?') ? '&' : '?') + 'autoplay=true&muted=false' : '';
  if (playing && src) {
    return <div className="relative aspect-video rounded-2xl overflow-hidden bg-black" data-id="element-909" data-mp="16ky1aw7drkp8">
        <iframe src={src} className="absolute inset-0 w-full h-full" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen title="Video" data-id="element-910" data-mp="2r3a12uh0auq6" />
      </div>;
  }
  return <div role="button" tabIndex={0} aria-label="Reproducir video de Chale" onClick={() => setPlaying(true)} onKeyDown={e => {
    if (e.key === 'Enter') setPlaying(true);
  }} className="relative aspect-video rounded-2xl overflow-hidden bg-[#0B0D12] cursor-pointer" data-id="element-911" data-mp="30wqsjrto7xqo">
      {poster && <img src={poster} alt="" width={1280} height={720} decoding="async" className="absolute inset-0 w-full h-full object-cover object-[50%_20%]" data-id="element-912" data-mp="35t7i8qgiqk5v" />}
      <div className="absolute inset-0 bg-black/35" data-id="element-913" data-mp="3jro0nenqtz3u" />
      <span className="absolute left-4 top-4 sm:left-6 sm:top-5 inline-flex items-center gap-2 text-white font-extrabold uppercase tracking-tight text-[15px] sm:text-[26px]" data-id="element-914" data-mp="3evyxz2gfg23b">
        Mensaje de {HOST_FIRST}
        <span className="h-3 w-3 sm:h-4 sm:w-4 rounded-full bg-red-600" aria-hidden="true" data-id="element-915" data-mp="1ki7oc2y3li7v" />
      </span>
      <UnmuteBox data-id="element-916" data-mp="3kkqfqps0ijkw" />
      {!src && <span className="absolute bottom-2 right-3 text-[10px] font-semibold text-white/60" data-id="element-917" data-mp="3pi8rsp1btex4">[Pega el link del video en funnel/config.ts]</span>}
    </div>;
}
function VideoSlot() {
  const yt = youtubeId(TYP_YOUTUBE_URL);
  return yt ? <YouTubeSlot id={yt} data-id="element-918" data-mp="2eluim66auvw" /> : <GenericSlot data-id="element-919" data-mp="2f3xe1ncc7cs" />;
}

/* PÁGINA DE AGENDA: video explicativo + calendario (sin bloquear) */
export function ThankYou() {
  const name = firstName();
  const calSrc = calendarSrc();
  useEffect(() => {
    track('CompleteRegistration');
    window.scrollTo(0, 0);
  }, []);
  useEffect(() => {
    if (calSrc) loadGhlEmbedScript();
  }, [calSrc]);
  return <div className="typ min-h-screen bg-white text-[#1a1a1a]" data-id="element-920" data-mp="l3sgqj66e4mg">
      <HeaderBar data-id="element-921" data-mp="l392pdjnc0dp" />
      <div className="px-3 sm:px-6 pt-6 sm:pt-10 pb-12" data-id="element-922" data-mp="l5v9haypqbuv">
        <div className="mx-auto max-w-[1000px] rounded-[28px] bg-white shadow-[0_0_30px_rgba(0,0,0,0.08)] px-4 py-6 sm:px-[7%] sm:py-12 text-center" data-id="element-923" data-mp="q4gahclsgzkd">
          {/* Igual que la referencia: el video arriba, primero que todo */}
          <VideoSlot data-id="element-924" data-mp="15426eo5zp14c" />

          <h2 className="mt-7 sm:mt-10 text-[24px] sm:text-[40px] font-extrabold uppercase leading-tight tracking-tight" data-id="element-925" data-mp="107brw59u2dl1">
            {name && <>{name} </>}<span className="inline-block" data-id="element-926" data-mp="v8qsu96bhzy6">¡Recibimos tu solicitud!</span>
          </h2>
          <p className="mt-2 text-[15px] sm:text-[22px] text-gray-700" data-id="element-927" data-mp="r7f8oj67q6xd">Mira el video y elige tu horario aquí abajo.</p>

          <div className="mt-6 sm:mt-8 mx-auto inline-block max-w-full bg-brand-light px-4 sm:px-14 py-3 sm:py-4 font-bold text-[15px] sm:text-[22px] leading-snug text-[#333]" data-id="element-928" data-mp="1nw4nu1qgglfn">
            Llamada 1:1 con {HOST_FIRST} <span className="block font-medium" data-id="element-929" data-mp="1e50epingnusq">20–30 minutos · desde tu computadora</span>
          </div>

          <p className="mt-5 sm:mt-7 text-[14px] sm:text-[22px] leading-[1.45]" data-id="element-930" data-mp="1a35b27i255bs">
            En el video te explico <strong data-id="element-931" data-mp="26oyw6ctkpfaw">cómo funciona todo, los costos y qué esperar</strong> de nuestra llamada. <strong data-id="element-932" data-mp="21v4etg1fxcez">Después elige tu horario</strong> y te escribo por <strong data-id="element-933" data-mp="2lk6bymk9qw2q">WhatsApp para confirmar.</strong>
          </p>

          <div id="agenda" className="mt-8 text-left" data-id="element-934" data-mp="2lilk54pc9r8k">
            {calSrc ? <iframe src={calSrc} id="ghl-booking-calendar" title={`Calendario de ${HOST_FIRST}`} className="w-full min-h-[760px] rounded-xl border-0 overflow-hidden" scrolling="no" data-id="element-935" data-mp="2qd97v1ciqhm3" /> : <div className="min-h-[420px] rounded-xl border-2 border-dashed border-brand/40 bg-brand-light flex items-center justify-center text-center px-6 text-sm font-semibold text-brand/70" data-id="element-936" data-mp="2gj8y2m6ako71">[CALENDARIO GHL — pega el link del widget en GHL_CALENDAR_EMBED_URL, funnel/config.ts]</div>}
          </div>

          <p className="mt-6 sm:mt-8 text-[13px] sm:text-[20px]" data-id="element-937" data-mp="2zbulqj71p8gj">
            <em className="bg-brand-light px-1 not-italic" data-id="element-938" data-mp="2za01l73llkdi">(<strong data-id="element-939" data-mp="1ix35t5r35to4">Importante</strong> — las personas que ven el video antes de la llamada son <span className="inline-block" data-id="element-940" data-mp="1nwo6plptgc3h">las que más la aprovechan.)</span></em>
          </p>
          <p className="mt-4 text-[13px] text-gray-500" data-id="element-941" data-mp="5z2mh0h6m3tb">Agendar una llamada no garantiza aceptación, licencia ni ingresos.</p>
          <p className="mt-2 text-[14px]" data-id="element-942" data-mp="10hm911xjk4v"><a href={WHATSAPP_URL} className="text-brand font-bold underline underline-offset-4" data-id="element-943" data-mp="z6kf6s1omge">¿Prefieres escribirme por WhatsApp?</a></p>

          <div className="mt-5 sm:mt-7" data-id="element-944" data-mp="exejbk8n5xu7">
            <p className="typ-signature text-[40px] sm:text-[64px] leading-none text-brand" data-id="element-945" data-mp="az744tttt4l4">{HOST_FIRST}</p>
          </div>
        </div>
      </div>
      <Footer data-id="element-946" data-mp="3jbl4e2iqz0z6" />
    </div>;
}

/* CONFIRMADA: redirección post-reserva desde GHL a #/confirmada */
export function Confirmed() {
  const name = firstName();
  useEffect(() => {
    track('Schedule');
    window.scrollTo(0, 0);
  }, []);
  return <div className="typ min-h-screen bg-white text-[#1a1a1a]" data-id="element-947" data-mp="3nfaq8k81f4gi">
      <HeaderBar data-id="element-948" data-mp="3ndpuwsdwfhhl" />
      <div className="px-3 sm:px-6 pt-6 sm:pt-10 pb-12" data-id="element-949" data-mp="2zpq7a1bvw778">
        <div className="mx-auto max-w-[1000px] rounded-[28px] bg-white shadow-[0_0_30px_rgba(0,0,0,0.08)] px-4 py-8 sm:px-[7%] sm:py-12 text-center" data-id="element-950" data-mp="2zo5didxe8saf">
          <h2 className="text-[24px] sm:text-[40px] font-bold uppercase leading-tight tracking-tight" data-id="element-951" data-mp="7ei9oxfmqzam">
            {name && <>{name} </>}<span className="inline-block" data-id="element-952" data-mp="2g6zgllfamrv">¡Tu llamada está confirmada!</span>
          </h2>
          <div className="mt-5 sm:mt-7 mx-auto inline-block max-w-full bg-brand-light px-4 sm:px-14 py-3 sm:py-4 font-bold text-[15px] sm:text-[22px] leading-snug text-[#333]" data-id="element-953" data-mp="3uhn43ktm86uy">
            Llamada 1:1 con {HOST_NAME}
          </div>
          <p className="mt-5 sm:mt-7 text-[14px] sm:text-[22px] leading-[1.45]" data-id="element-954" data-mp="3kmld0jfs4upq">
            <strong data-id="element-955" data-mp="3fs8t68ae5hdg">Revisa tu WhatsApp:</strong> te escribo para confirmar. <strong data-id="element-956" data-mp="3au767c9ugxwp">Si no respondes, liberamos tu espacio.</strong>
            <br data-id="element-957" data-mp="3asckhvqr11rk" />Conéctate <strong data-id="element-958" data-mp="36tdkmpjvjv6z">desde computadora</strong>, en un lugar tranquilo, y trae tus preguntas sobre <strong data-id="element-959" data-mp="36raodns4p5tt">costos, licencia y horarios.</strong>
          </p>
          <p className="mt-6 sm:mt-8 text-[13px] sm:text-[20px]" data-id="element-960" data-mp="31thd4gbyxlmo">
            <em className="bg-brand-light px-1 not-italic" data-id="element-961" data-mp="2wuo1yuk5m6po">(<strong data-id="element-962" data-mp="2wx0s3v02fndt">Importante</strong> — si aún no viste el video, míralo antes de la llamada.)</em>
          </p>
          <div className="mt-5 sm:mt-7" data-id="element-963" data-mp="1hbzwfciefyxb"><p className="typ-signature text-[40px] sm:text-[64px] leading-none text-brand" data-id="element-964" data-mp="1hb6y8zxiahqs">{HOST_FIRST}</p></div>
        </div>
      </div>
      <Footer data-id="element-965" data-mp="1mbkvdmxkk7a1" />
    </div>;
}